import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaClock, FaCalendarAlt, FaUsers, FaStar, FaCheckCircle, FaArrowRight,
  FaBookOpen, FaCertificate, FaTrophy, FaGlobe, FaAward, FaGraduationCap,
  FaClipboardCheck, FaLaptop, FaChevronDown, FaChevronUp, FaRupeeSign,
  FaExternalLinkAlt, FaListUl
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 180px;
  background: #ffffff;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-top: 155px;
  }


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

/* ========== HERO ========== */
const HeroSection = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #3730A3 50%, #0b0b77 100%);
  color: white;
  padding: 2.5rem 5%;
  text-align: center;
`;

const HeroBadge = styled.span`
  display: inline-flex; align-items: center; gap: 0.5rem;
  background: rgba(255,255,255,0.1); padding: 0.4rem 1.2rem; border-radius: 50px;
  font-size: 0.9rem; font-weight: 600; margin-bottom: 1rem;
  svg { color: #a78bfa; }
`;

const HeroTitle = styled.h1`
font-family: 'Times New Roman', serif;
  font-size: 2.8rem; font-weight: 800; margin-bottom: 0.5rem;
  span { display: block; color: #a78bfa; font-size: 1.2rem; font-weight: 400; margin-top: 0.2rem; }
  @media (max-width: 768px) { font-size: 2rem; span { font-size: 1rem; } }
`;

const HeroDesc = styled.p`
font-family: 'Times New Roman', serif;
  font-size: 1rem; opacity: 0.85; max-width: 650px; margin: 0.5rem auto 1rem;
`;

const HeroInfo = styled.div`
  display: flex; justify-content: center; gap: 1.5rem; flex-wrap: wrap; margin-top: 1rem;
  div { display: flex; align-items: center; gap: 0.4rem; background: rgba(255,255,255,0.08); padding: 0.5rem 1rem; border-radius: 30px; font-size: 0.9rem; 
    svg { color: #a78bfa; } strong { color: #a78bfa; } }
`;

/* ========== CONTAINER ========== */
const Container = styled.div`
  max-width: 1100px; margin: 0 auto; padding: 2.5rem 5% 3rem;
`;

/* ========== LAYOUT ========== */
const TwoCol = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-bottom: 1.5rem;
  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

const ThreeCol = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-bottom: 1.5rem;
  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

/* ========== CARDS ========== */
const SectionCard = styled.div`
  background: white; border-radius: 14px; padding: 1.5rem;
  border: 1px solid #f0f0f5; box-shadow: 0 2px 10px rgba(0,0,0,0.02);
`;

const CardTitle = styled.h3`
font-family: 'Times New Roman', serif;
  font-size: 1.1rem; color: #1a1a2e; font-weight: 700; margin-bottom: 1rem;
  display: flex; align-items: center; gap: 0.6rem;
  .card-icon { width: 36px; height: 36px; border-radius: 10px; background: #f5f3ff; color: #3730A3; display: flex; align-items: center; justify-content: center; font-size: 1rem; }
`;

const SimpleList = styled.ul`
font-family: 'Times New Roman', serif;
  list-style: none; padding: 0;
  li { display: flex; align-items: flex-start; gap: 0.6rem; padding: 0.4rem 0; color: #4a4a6a; font-size: 0.9rem; line-height: 1.5;
    svg { color: #3730A3; font-size: 0.85rem; margin-top: 3px; flex-shrink: 0; } }
`;

const NumberedList = styled.div`
  display: flex; flex-direction: column; gap: 0.5rem;
`;

const NumberedItem = styled.div`
font-family: 'Times New Roman', serif;
  display: flex; align-items: flex-start; gap: 0.8rem;
  .num { width: 28px; height: 28px; border-radius: 50%; background: #3730A3; color: white; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.8rem; flex-shrink: 0; }
  p { color: #4a4a6a; font-size: 0.9rem; line-height: 1.5; }
`;

const PracticeGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-bottom: 1.5rem;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const PracticeCard = styled.div`
font-family: 'Times New Roman', serif;
  background: white; border-radius: 12px; padding: 1.2rem; text-align: center;
  border: 1px solid #f0f0f5; box-shadow: 0 2px 8px rgba(0,0,0,0.02);
  .practice-icon { font-size: 1.5rem; color: #3730A3; margin-bottom: 0.5rem; }
  h4 { font-size: 0.95rem; color: #1a1a2e; margin-bottom: 0.3rem; }
  p { font-size: 0.85rem; color: #4a4a6a; }
`;

/* ========== CURRICULUM COLLAPSIBLE ========== */
const CurrCard = styled.div`
  background: white; border-radius: 12px; margin-bottom: 0.6rem;
  border: 1px solid #f0f0f5; overflow: hidden;
`;

const CurrHeader = styled.div`
font-family: 'Times New Roman', serif;
  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 1.2rem; background: ${props => props.open ? '#f5f3ff' : '#fafbff'}; cursor: pointer;
  transition: all 0.3s ease;
  &:hover { background: #f5f3ff; }
  .curr-left { display: flex; align-items: center; gap: 0.8rem; font-weight: 700; color: #1a1a2e; font-size: 0.95rem;
    svg { color: #3730A3; } }
  .curr-arrow { color: #3730A3; transition: transform 0.3s ease; }
`;

const CurrBody = styled.div`
font-family: 'Times New Roman', serif;
  padding: ${props => props.open ? '1rem 1.2rem' : '0 1.2rem'};
  max-height: ${props => props.open ? '800px' : '0'}; overflow: hidden; transition: all 0.3s ease;
  ul { list-style: none; padding: 0;
    li { display: flex; align-items: center; gap: 0.5rem; padding: 0.4rem 0; color: #4a4a6a; font-size: 0.85rem; border-bottom: 1px solid #f5f5f8;
      &:last-child { border-bottom: none; }
      &::before { content: '▸'; color: #3730A3; font-weight: bold; } } }
`;

/* ========== CERT BLOCK ========== */
const CertBlock = styled.div`
  background: #fff;
  border: 1px solid #f0f0f5;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const CertHead = styled.div`
  background: #3730A3;
  padding: 1.2rem 1.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const CertHeadTitle = styled.div`
  font-family: 'Times New Roman', serif;
  font-size: 1.1rem;
  color: #fff;
  font-weight: 800;
`;

const CertHeadBadge = styled.div`
  font-family: 'Times New Roman', serif;
  background: rgba(167, 139, 250, 0.2);
  color: #a78bfa;
  border: 1px solid rgba(167, 139, 250, 0.3);
  font-size: 0.72rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 0.3rem 0.8rem;
  border-radius: 4px;
  font-weight: 700;
`;

const CertBody = styled.div`
  padding: 1.8rem;
`;

const CertDesc = styled.p`
  font-family: 'Times New Roman', serif;
  font-size: 0.95rem;
  color: #4a4a6a;
  line-height: 1.8;
  margin-bottom: 1.8rem;
  border-left: 3px solid #a78bfa;
  padding-left: 1.2rem;
`;

const CertCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.8rem;

  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

const CertColTitle = styled.div`
  font-family: 'Times New Roman', serif;
  font-size: 0.78rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #3730A3;
  font-weight: 700;
  margin-bottom: 0.8rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PlainList = styled.ul`
  list-style: none;
  padding: 0;

  li {
    font-family: 'Times New Roman', serif;
    font-size: 0.92rem;
    color: #4a4a6a;
    padding: 0.35rem 0;
    border-bottom: 1px solid #f0f0f5;
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    line-height: 1.5;

    &:last-child { border-bottom: none; }

    svg { color: #3730A3; font-size: 0.8rem; margin-top: 3px; flex-shrink: 0; }
  }
`;

const FeeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Times New Roman', serif;
  margin-bottom: 0;

  th {
    background: #1a1a2e;
    color: rgba(255,255,255,0.7);
    font-size: 0.75rem;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 0.7rem 1.2rem;
    text-align: left;
    font-weight: 700;
  }

  td {
    padding: 0.9rem 1.2rem;
    border-bottom: 1px solid #f0f0f5;
    font-size: 0.95rem;
    color: #4a4a6a;
  }

  tr:last-child td { border-bottom: none; }

  .amt {
    font-size: 1.5rem;
    font-weight: 900;
    color: #1a1a2e;
  }

  tr.highlight { background: #f5f3ff; }
  tr.highlight .amt { color: #3730A3; }
  tr.highlight td { border-bottom: none; }
`;

/* ========== CTA ========== */
const CTASection = styled.div`
  margin: 2rem 0 1rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: #1a1a2e;
  border-radius: 14px;
  overflow: hidden;
  padding: 1.5rem 1.5rem 1.5rem 0;

  &::before {
    content: '';
    width: 5px;
    align-self: stretch;
    background: #a78bfa;
    flex-shrink: 0;
  }

  @media (max-width: 650px) {
    flex-direction: column;
    padding: 1.5rem;
    &::before { display: none; }
  }
`;

const CTAText = styled.div`
  flex: 1;
  .cta-title {
    font-family: 'Times New Roman', serif;
    font-size: 1.4rem;
    font-weight: 800;
    color: #fff;
    margin-bottom: 0.3rem;
  }
  .cta-sub {
    font-family: 'Times New Roman', serif;
    font-size: 0.95rem;
    color: rgba(255,255,255,0.55);
    font-style: italic;
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #a78bfa;
  color: #1a1a2e;
  font-family: 'Times New Roman', serif;
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.8rem 1.8rem;
  border-radius: 50px;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover { 
    background: #c4b5fd; 
    transform: translateY(-2px);
  }
`;

const Spacer = styled.div`
  height: 2rem;
`;

const AdvancedCoding = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [openModules, setOpenModules] = useState({});

  const toggleModule = (index) => {
    setOpenModules(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const topics = [
    "Overview of U.S. Healthcare", "Overview of Reimbursement Methodologies",
    "Medical Record Documentation", "US Healthcare Legal & Regulatory Guidelines",
    "Compliance, Edits, & Reports", "Advanced ICD-10-CM Coding",
    "Advanced CPT Coding", "Advanced HCPCS Coding"
  ];

  const practice = [
    { icon: <FaClipboardCheck />, title: "Exam Practice", desc: "4 Mock Papers" },
    { icon: <FaLaptop />, title: "Section Wise Practice", desc: "20 to 30 Hours" },
    { icon: <FaStar />, title: "Review & Feedback", desc: "12 Hours" }
  ];

  const benefits = [
    "Long-term stable career with opportunities to work onsite in US, Middle East and other countries",
    "Comprehensive knowledge of hospital and physician practice coding",
    "Improves earning potential",
    "By gaining this certification, you can improve your potential for employability",
    "Getting certified gives you confidence on the subject which helps your overall performance",
    "Track record of passing rate of more than 98% apart from Trainee satisfaction",
    "Placement assistance provided for merit candidates"
  ];

  const whoShouldAttend = [
    "Medical, Paramedical and Life Science Graduates & Post Graduates (M.B.B.S, B.D.S, B.H.M.S, B.A.M.S, B.U.M.S, B.S.M.S, Pharmacy, Physiotherapy, Nursing, Occupational Therapy, Microbiology, Biochemistry, Biotechnology, Genetics, Biology, Bio-Medical graduates, Zoology, Botany, Bioinformatics, Endocrinology, Nutrition & Dietetics, Anatomy, Physiology, Health Education, etc.)",
    "Candidates already working in outpatient settings like ED Professional/Facility, E&M Coding, etc., who are willing to advance their career in medical coding",
    "Candidates working in HCC coding, who are willing to explore other options in their career",
    "Candidates who are willing to learn inpatient DRG Coding",
    "Candidates who are willing to move to Middle East for opportunities in medical coding",
    "Candidates who are already working in Middle East and willing to advance their coding to inpatient coding",
    "Candidates who are willing to move to U.S. for employment opportunities or any other reason"
  ];

  const curriculum = [
    { title: "Overview of U.S. Healthcare", items: ["Revenue Cycle Management", "Different types of Insurance Providers", "Different types of Billing forms"] },
    { title: "Overview of Reimbursement Methodologies", items: ["Inpatient Prospective Payment System (IPPS)", "Diagnostic Related Groups (DRG)", "DRG Relative Weights", "Payment Adjustments", "Outlier Payments", "Transfer Policies", "Outpatient Prospective Payment System (OPPS)", "Resource-Based Relative Value System (RBRVS)", "Ambulatory Payment Classification"] },
    { title: "Medical Record Documentation", items: ["JCAHO Guidelines for Medical Record Documentation & Standards", "UHDDS Definitions and Practical Application"] },
    { title: "U.S. Healthcare Legal & Regulatory Guidelines", items: ["Affordable Care Act", "Health Insurance Portability and Accountability Act (HIPAA)", "Health Information Technology for Economic and Clinical Health Act (HITECH)"] },
    { title: "Compliance, Edits & Reports", items: ["Compliance Policies for Hospital and Physician Practices", "Corporate Integrity Agreement", "National Correct Coding Initiative (NCCI)", "Medicare Code Edits (MCE)", "Outpatient Code Edits (OCE)", "Medically Unlikely Edits (MUE)", "FATHOM, PEPPER, CERT, CBR Reports", "Coding Clinic Updates", "CPT Assistant"] },
    { title: "ICD-10-CM Coding (Inpatient & Outpatient)", items: ["Structure, Format, and Conventions", "General Coding Guidelines", "Chapter Specific Guidelines"] },
    { title: "CPT Coding (Outpatient Physician/Facility)", items: ["Structure, Format, and Sections of CPT", "Evaluation & Management Coding", "Surgical Package", "Modifiers and their practical application", "Surgery Coding", "Radiology Coding", "Laboratory and Pathology Services Coding", "Coding from the Medicine Section"] },
    { title: "HCPCS Code Set", items: ["Reporting medical supplies and equipment and other services"] },
    { title: "ICD-10-PCS Coding (Inpatient)", items: ["Structure and Format of PCS Codes", "PCS Coding Guidelines", "Different Sections of PCS Coding", "Root Operation Groups"] },
    { title: "Practice Modules", items: ["Practical Case Scenarios for Outpatient Coding", "Practical Case Scenarios for Inpatient Coding"] }
  ];

  const inclusions = [
    '80–100 Hours Expert Training',
    'Study Materials & Practice Exercises',
    'Mock Exams (AHIMA Pattern)',
    'One-on-One Mentoring',
    'Placement Assistance',
    'Certificate of Completion'
  ];

  return (
    <PageContainer>
      {/* HERO */}
      <HeroSection>
        <HeroBadge><FaAward /> Advanced Coding Program</HeroBadge>
        <HeroTitle>Advanced Coding Training<span>Master Inpatient & Outpatient Coding</span></HeroTitle>
        <HeroDesc>
          Comprehensive training covering ICD-10-CM, CPT, HCPCS, and ICD-10-PCS. Prepares you for advanced coding certification.
        </HeroDesc>
        <HeroInfo>
          <div><FaClock /> Duration: <strong>80-100 Hours</strong></div>
          <div><FaCalendarAlt /> <strong>10-12 Weeks</strong></div>
          <div><FaTrophy /> Pass Rate: <strong>98%</strong></div>
          <div><FaGlobe /> <strong>Global Recognition</strong></div>
        </HeroInfo>
      </HeroSection>

      <Container>
        {/* ROW 1: Topics + Practice */}
        <TwoCol>
          <SectionCard>
            <CardTitle><span className="card-icon"><FaBookOpen /></span>Topics Covered</CardTitle>
            <SimpleList>
              {topics.map((t, i) => <li key={i}><FaCheckCircle /> {t}</li>)}
            </SimpleList>
          </SectionCard>

          <SectionCard>
            <CardTitle><span className="card-icon"><FaStar /></span>Practice Structure</CardTitle>
            <PracticeGrid>
              {practice.map((p, i) => (
                <PracticeCard key={i}>
                  <div className="practice-icon">{p.icon}</div>
                  <h4>{p.title}</h4>
                  <p>{p.desc}</p>
                </PracticeCard>
              ))}
            </PracticeGrid>
          </SectionCard>
        </TwoCol>

        {/* BENEFITS */}
        <SectionCard style={{marginBottom:'1.5rem'}}>
          <CardTitle><span className="card-icon"><FaTrophy /></span>Program Benefits</CardTitle>
          <SimpleList>
            {benefits.map((b, i) => <li key={i}><FaCheckCircle /> {b}</li>)}
          </SimpleList>
        </SectionCard>

        {/* WHO SHOULD ATTEND */}
        <SectionCard style={{marginBottom:'1.5rem'}}>
          <CardTitle><span className="card-icon"><FaUsers /></span>Who Should Attend?</CardTitle>
          <NumberedList>
            {whoShouldAttend.map((item, i) => (
              <NumberedItem key={i}><span className="num">{i+1}</span><p>{item}</p></NumberedItem>
            ))}
          </NumberedList>
        </SectionCard>

        {/* CURRICULUM - Collapsible */}
        <SectionCard style={{marginBottom:'1.5rem'}}>
          <CardTitle><span className="card-icon"><FaBookOpen /></span>Course Curriculum</CardTitle>
          <p style={{color:'#4a4a6a', fontSize:'0.85rem', marginBottom:'1rem'}}>Click on each module to expand and view detailed topics.</p>
          {curriculum.map((mod, i) => (
            <CurrCard key={i}>
              <CurrHeader open={openModules[i]} onClick={() => toggleModule(i)}>
                <div className="curr-left"><FaBookOpen /> {mod.title}</div>
                <span className="curr-arrow">{openModules[i] ? <FaChevronUp /> : <FaChevronDown />}</span>
              </CurrHeader>
              <CurrBody open={openModules[i]}>
                <ul>{mod.items.map((item, j) => <li key={j}>{item}</li>)}</ul>
              </CurrBody>
            </CurrCard>
          ))}
        </SectionCard>

        {/* CERTIFICATION & FEES */}
        <CertBlock>
          <CertHead>
            <CertHeadTitle>Certificate of Completion</CertHeadTitle>
            <CertHeadBadge>Training Certificate</CertHeadBadge>
          </CertHead>
          <CertBody>
            <CertDesc>
              A certificate of completion will be provided to every student who successfully completes 
              the training course and meets the minimum scoring percentage in the final assessment.
            </CertDesc>

            <CertCols>
              <div>
                <CertColTitle><FaAward /> What You'll Learn</CertColTitle>
                <PlainList>
                  <li><FaCheckCircle />Inpatient & Outpatient Coding</li>
                  <li><FaCheckCircle />ICD-10-CM & ICD-10-PCS</li>
                  <li><FaCheckCircle />CPT & HCPCS Coding</li>
                  <li><FaCheckCircle />Reimbursement Methodologies</li>
                  <li><FaCheckCircle />Compliance & Regulatory Guidelines</li>
                </PlainList>
              </div>
              <div>
                <CertColTitle><FaListUl /> Course Inclusions</CertColTitle>
                <PlainList>
                  {inclusions.map((inc, i) => (
                    <li key={i}><FaCheckCircle />{inc}</li>
                  ))}
                </PlainList>
              </div>
            </CertCols>

            <FeeTable>
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="highlight">
                  <td><strong>Course Fee</strong></td>
                  <td>To be paid to LearnThruUs Academy</td>
                  <td><span className="amt">₹20,000 + GST</span></td>
                </tr>
              </tbody>
            </FeeTable>
          </CertBody>
        </CertBlock>

        {/* CTA */}
        <CTASection>
          <CTAText>
            <div className="cta-title">Ready to Master Advanced Coding?</div>
            <div className="cta-sub">Join our Advanced Coding Training and prepare for advanced certification.</div>
          </CTAText>
          <CTAButton to="/contact-us">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>

        <Spacer />
      </Container>
    </PageContainer>
  );
};

export default AdvancedCoding;